import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { CmdbView, Pair } from './cmdb'
import type { ApprovalState, ChangeClash } from './changeManagement'

/**
 * Contractul ferestrelor de mentenanta (v4/api, App\Maintenance\*).
 *
 * Numele campurilor sunt exact cele pe care le scrie serverul. Cusatura dintre
 * cele doua jumatati s-a rupt de cinci ori saptamana asta - fiecare parte se
 * proba pe sine si amandoua treceau - deci forma raspunsului e inghetata intr-o
 * inregistrare comuna (`src/cmdb/__fixtures__/maintenance*.json`), citita si de
 * proba din browser, si de cea din PHP.
 *
 * Inregistrarea sta langa cea a CMDB-ului fiindca ecranul e al lui: ferestrele
 * sunt aprobarile date pe elementele din registru.
 */

/** Cele trei perioade dupa care se poate ingusta lista. */
export type MaintenanceWhen = '' | 'open' | 'upcoming' | 'past'

/** Cele trei stari ale unei ferestre, socotite din ceas. */
export type WindowState = 'open' | 'upcoming' | 'past'

export interface MaintenanceCounts {
  total: number
  open: number
}

/** Antetul ecranului - `Maintenance::index()`. */
export interface MaintenancePage {
  /** A ajuns migrarea. Fals inseamna "se spune", nu "se cade cu 1146". */
  hasTable: boolean
  counts: MaintenanceCounts
  when: MaintenanceWhen
  whens: WindowState[]
  scopes: Pair[]
  canWrite: boolean
  views: CmdbView[]
}

/** Un rand din lista - `Maintenance::windowsData()`. */
export interface WindowRow {
  ID: number
  name: string
  company_name: string
  scope_type: number
  /** Ce acopera, NUMIT: firma, clasa sau elementul, dupa `scope_type`. */
  scope_name: string
  scope_label: string
  starts_at: string
  ends_at: string
  /** 0 = nicio cerere de schimbare legata de fereastra asta. */
  ticket_id: number
  state: WindowState
  created_by: string
}

export interface WindowTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: WindowRow[]
  /** Cifrele de deasupra calatoresc cu randurile, ca sa nu ramana vechi. */
  counts: MaintenanceCounts
}

export interface WindowTableRequest {
  draw: number
  start: number
  length: number
  search: string
  when: MaintenanceWhen
  order_column: number
  order_dir: 'asc' | 'desc'
}

export function maintenancePageQuery(when: MaintenanceWhen) {
  return queryOptions({
    queryKey: ['maintenance', 'page', when],
    queryFn: () => apiGet<MaintenancePage>(`/maintenance?when=${encodeURIComponent(when)}`),
  })
}

export function maintenanceTableQuery(r: WindowTableRequest) {
  return queryOptions({
    queryKey: ['maintenance', 'table', r],
    queryFn: () => apiGet<WindowTablePage>(`/maintenance/table?${maintenanceTableParams(r).toString()}`),
  })
}

export function maintenanceTableParams(r: WindowTableRequest): URLSearchParams {
  const params = new URLSearchParams()
  params.set('draw', String(r.draw))
  params.set('start', String(r.start))
  params.set('length', String(r.length))
  if (r.search !== '') params.set('search[value]', r.search)
  if (r.when !== '') params.set('when', r.when)
  params.set('order_column', String(r.order_column))
  params.set('order_dir', r.order_dir)

  return params
}

/* --- Calendarul, ziua, formularul si scrierile ------------------------ */

/** Un rand al calendarului / al zilei - `Maintenance_windows::getWindows()`. */
export interface CalendarWindow extends WindowRow {
  company_id: number
  scope_id: number
  starts_at_full: string
  ends_at_full: string
}

export interface Conflict {
  id: number
  name: string
  reason: string
}

/** Un change planificat, in calendar - `MaintenanceWindows::changeRow()`. */
export interface CalendarChange {
  ticket_id: number
  subject: string
  company_id: number
  change_class: number
  class_label: string
  approval_state: ApprovalState
  starts_at: string
  ends_at: string
  starts_at_full: string
  ends_at_full: string
  cis: string[]
}

export interface MaintenanceCalendar {
  month: string
  gridStart: string
  cells: number
  windows: CalendarWindow[]
  conflicts: Record<string, Conflict[]>
  /** Change-urile planificate in luna, cu starea aprobarii. */
  changes: CalendarChange[]
  /** Ciocnirile change-urilor: cu alt change sau cu o fereastra, pe acelasi element. */
  changeClashes: ChangeClash[]
  canWrite: boolean
  views: CmdbView[]
}

export interface MaintenanceDay {
  date: string
  windows: CalendarWindow[]
  conflicts: Record<string, Conflict[]>
  canWrite: boolean
  views: CmdbView[]
}

export interface WindowRecord {
  ID: number
  company_id: number
  name: string
  starts_at: string
  ends_at: string
  scope_type: number
  scope_id: number
  ticket_id: number
}

export interface Office {
  ID: number
  Nume: string
}

/** `Maintenance::windowForm()`. */
export interface WindowForm {
  window: WindowRecord | null
  scopeCi: { id: number; name: string } | null
  offices: Office[]
  pickedOffices: number[]
  day: string
  hour: number | null
  companies: Office[]
  classes: { ID: number; name: string }[]
  scopes: Pair[]
  canWrite: boolean
}

export interface CiHit {
  id: number
  name: string
  class: string
}

export interface SaveAnswer {
  status: 'OK' | 'ERROR'
  id?: number
  message: string
  warning?: boolean
}

export function maintenanceCalendarQuery(month: string) {
  return queryOptions({
    queryKey: ['maintenance', 'calendar', month],
    queryFn: () => apiGet<MaintenanceCalendar>(`/maintenance/calendar?month=${encodeURIComponent(month)}`),
  })
}

export function maintenanceDayQuery(date: string) {
  return queryOptions({
    queryKey: ['maintenance', 'day', date],
    queryFn: () => apiGet<MaintenanceDay>(`/maintenance/day?date=${encodeURIComponent(date)}`),
  })
}

export function windowFormQuery(id: number, day = '', hour: number | null = null) {
  const params = new URLSearchParams()
  if (day !== '') params.set('day', day)
  if (hour !== null) params.set('hour', String(hour))
  const suffix = params.toString() === '' ? '' : `?${params.toString()}`
  return queryOptions({
    queryKey: ['maintenance', 'form', id, day, hour],
    queryFn: () => apiGet<WindowForm>(`/maintenance/form/${id}${suffix}`),
    staleTime: 0,
  })
}

export function officesFor(companyId: number): Promise<Office[]> {
  return apiGet<Office[]>(`/maintenance/offices?company_id=${companyId}`)
}

export function ciSearch(term: string, companyId: number): Promise<CiHit[]> {
  return apiGet<CiHit[]>(`/maintenance/ci-search?q=${encodeURIComponent(term)}&company_id=${companyId}`)
}

export function saveWindow(fields: Record<string, PostValue>): Promise<SaveAnswer> {
  return apiPost<SaveAnswer>('/maintenance/save', fields)
}

export function removeWindow(id: number): Promise<SaveAnswer> {
  return apiPost<SaveAnswer>(`/maintenance/${id}/remove`, {})
}
